/**
 * Audit Methodology section (EditProposal) — three related, independently
 * testable pieces of logic pulled out of the giant component so they can be
 * unit tested without mounting it:
 *
 * 1. HO Address prefill — the renamed "Site Address / Site Summary" field
 *    (label only, state key `siteAddress` is unchanged) now auto-fills from
 *    the client Company's own address, but only while the proposal's field
 *    is still blank — never clobbers a value the user already typed/saved.
 * 2. Company address sync payload — when the user edits HO Address, that
 *    edit must be written back to the Company record. `PUT /md/companies/{id}`
 *    is a full-overwrite endpoint (see ControllerDataMaster#updateCompany) —
 *    sending a partial body with only `address` set would null out
 *    companyName/sector/etc., and an omitted `contacts` array causes the
 *    backend to DELETE every existing contact (the update loop keys off
 *    `updatedContactIds`, which stays empty when contacts is absent). This
 *    builder always echoes back the full company record + full contacts list
 *    fetched from GET /md/companies/{id}, with only `address` overridden —
 *    a true partial-field edit, never a partial-payload write.
 * 3. Methodology auto-select — a proposal whose selected services carry
 *    exactly one audit standard in total has no possible "Integrated" or
 *    "Combined" methodology (both require 2+ standards), so the field is
 *    forced to "Single Audit" and locked. The moment a second standard is
 *    added, the field reverts to freely editable — matching the existing
 *    Integrated/Combined section, which already only renders when
 *    getAuditStandardOptions().length > 1.
 */

export const resolveHoAddressPrefill = (currentSiteAddress, companyAddress) => {
  const hasCompanyAddress = companyAddress && String(companyAddress).trim();
  return hasCompanyAddress ? companyAddress : currentSiteAddress;
};

/**
 * True only when the address actually changed and there is a company to
 * write it back to — the caller uses this to decide whether a PUT is worth
 * issuing at all (e.g. on blur), avoiding a no-op write on every focus/blur.
 */
export const shouldSyncHoAddressToCompany = (companyId, previousAddress, nextAddress) => {
  if (!companyId) return false;
  const prev = String(previousAddress || '').trim();
  const next = String(nextAddress || '').trim();
  return prev !== next;
};

/**
 * Builds the full CompanyWithContactsDTO-shaped payload for
 * `PUT /md/companies/{id}`, starting from the company object + contacts
 * array as returned by `GET /md/companies/{id}` (`{ company, contacts }`),
 * with only `address` overridden. Every other field — including
 * `taggingIds`/`branchId`/`defaultCurrencyId` and, critically, the full
 * `contacts` array — is echoed back unchanged so the backend's
 * full-overwrite semantics never silently wipe unrelated company data.
 */
export const buildCompanyAddressUpdatePayload = (company, contacts, newAddress) => {
  const safeCompany = company || {};
  const safeContacts = Array.isArray(contacts) ? contacts : [];
  return {
    companyName: safeCompany.companyName,
    businessPartnerType: safeCompany.businessPartnerType,
    sector: safeCompany.sector,
    companySize: safeCompany.companySize,
    website: safeCompany.website,
    companyEmail: safeCompany.companyEmail,
    phone: safeCompany.phone,
    npwp: safeCompany.npwp,
    businessLocation: safeCompany.businessLocation,
    address: newAddress,
    country: safeCompany.country,
    state: safeCompany.state,
    city: safeCompany.city,
    postalCode: safeCompany.postalCode,
    entityType: safeCompany.entityType,
    defaultCurrencyId: safeCompany.defaultCurrencyId,
    taggingIds: Array.isArray(safeCompany.taggingIds) ? safeCompany.taggingIds : [],
    branchId: safeCompany.branchId,
    contacts: safeContacts,
  };
};

/**
 * Total distinct audit standards selected across every service section on
 * the proposal — the same count `getAuditStandardOptions()` already
 * produces, extracted here as a pure function of that array's length so the
 * methodology auto-select rule is testable on its own.
 */
export const resolveAutoMethodology = (standardCount, currentMethodology) => {
  const count = Number(standardCount) || 0;
  if (count === 1) {
    return { value: 'Single Audit', isLocked: true };
  }
  return { value: currentMethodology, isLocked: false };
};

/**
 * `buildSiteFromCompany`'s Site Name used to hardcode `'Head Office'`
 * regardless of real company data. Site Location must no longer auto-fill a
 * display name at all — the user names the site themselves, since it is the
 * literal record that gets seeded to Audit Platform for scheduling and
 * certificate issuance/draft (a site is not always literally "Head Office").
 */
export const resolvePrimarySiteName = () => '';
